import { FunctionComponent, useState, useEffect } from 'react'
import {
  AppBar,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import { getAppConfig } from '../../config/appConfig'
import ApexLogoWhite from '../../images/apex_logo_white.svg'
import { Typography, ThemeConfig } from 'apex-ui-kit-pcn'
import { PcnRedirect } from '../PcnRedirect'
import { clearSearchCriteriaAction } from '../../store/slices/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

const PREFIX = 'NavBar'

const classes = {
  list: `${PREFIX}-list`,
  listItemButton: `${PREFIX}-listItemButton`,
  listItemIcon: `${PREFIX}-listItemIcon`,
  listItemText: `${PREFIX}-listItemText`,
  toolBar: `${PREFIX}-toolBar`,
  subHeader: `${PREFIX}-subHeader`,
  paper: `${PREFIX}-paper`,
  apexLogo: `${PREFIX}-apexLogo`,
}

const StyledDrawer = styled(Drawer)({
  [`& .${classes.list}`]: { width: 300 },

  [`& .${classes.listItemButton}`]: {
    '&:hover': { backgroundColor: 'rgba(158,158,158,0.2)' },
  },

  [`& .${classes.listItemIcon}`]: { color: 'inherit', minWidth: '30px' },

  [`& .${classes.listItemText}`]: {
    color: 'inherit',
    textTransform: 'uppercase',
  },

  [`& .${classes.subHeader}`]: {
    color: 'inherit',
    backgroundColor: 'inherit',
    opacity: 0.55,
  },
  [`& .${classes.paper}`]: {
    backgroundColor: ThemeConfig.palette.backgroundLayout.sideMenu,
    color: '#ccc',
  },
})

const StyledToolbar = styled(Toolbar)({
  [`&.${classes.toolBar}`]: { backgroundColor: '#8F1C20', padding: '8px' },

  [`& .${classes.apexLogo}`]: {
    width: '72px',
    height: '72px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
})

interface NavBarProps {
  navRoutes: { [key: string]: any }
  clearSearchCriteriaOnNavigate?: boolean
}

const NavBar: FunctionComponent<NavBarProps> = ({
  navRoutes,
  clearSearchCriteriaOnNavigate,
}) => {
  const dispatch = useAppDispatch()
  const pageName: string = useAppSelector(state => state.navBar.pageName)

  const [open, setOpen] = useState<boolean>(false)
  const [redirectTo, setRedirectTo] = useState<string>('none')

  useEffect(() => {
    if (redirectTo !== 'none') {
      setOpen(false)
      setRedirectTo('none')
    }
  }, [redirectTo])

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const onNavLinkClick = pageName => {
    setOpen(false)
    const newRedirection = navRoutes[pageName].route
    if (newRedirection !== redirectTo) {
      if (newRedirection === '/logout') {
        const config = getAppConfig()
        localStorage.removeItem('id_token')
        localStorage.removeItem('access_token')
        window.location.href = config.auth.logoutUrl
      } else {
        if (clearSearchCriteriaOnNavigate) {
          dispatch(clearSearchCriteriaAction())
        }
        setRedirectTo(newRedirection)
      }
    }
  }

  return (
    <>
      {redirectTo !== 'none' && <PcnRedirect redirectTo={redirectTo} />}
      {redirectTo === 'none' && (
        <AppBar position={'static'}>
          <StyledToolbar className={classes.toolBar}>
            <IconButton
              color="inherit"
              aria-label="Menu"
              onClick={toggleDrawer}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" color="inherit">
              {pageName}
            </Typography>
          </StyledToolbar>
          <StyledDrawer
            open={open}
            onClose={toggleDrawer}
            classes={{ paper: classes.paper }}
          >
            <div className={classes.list}>
              <StyledToolbar className={classes.toolBar}>
                <ApexLogoWhite className={classes.apexLogo} alt={'Apex Logo'} />
              </StyledToolbar>
              <List>
                <ListSubheader className={classes.subHeader}>
                  Navigation
                </ListSubheader>
                {Object.keys(navRoutes).map(pageName => (
                  <ListItem
                    button
                    key={pageName}
                    onClick={() => onNavLinkClick(pageName)}
                    classes={{ button: classes.listItemButton }}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      {navRoutes[pageName].icon}
                    </ListItemIcon>
                    <ListItemText
                      classes={{ primary: classes.listItemText }}
                      primary={pageName}
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          </StyledDrawer>
        </AppBar>
      )}
    </>
  )
}

export default NavBar
